import { useEffect, useMemo, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import DOMPurify from "dompurify";
import api, { API } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  Bold, Italic, Underline as UnderlineIcon, Heading2, Heading3, List,
  Loader2, Sparkles, Save, FileDown, FileText, AlertTriangle,
} from "lucide-react";

function countPlaceholders(html) {
  if (!html) return 0;
  const matches = html.match(/\[INSERIR[^\]]*\]/gi);
  return matches ? matches.length : 0;
}

export default function PetitionEditor({ caseData, onSaved }) {
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [html, setHtml] = useState(caseData.petition_html || "");

  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: caseData.petition_html || "<p></p>",
    editorProps: {
      attributes: { class: "petition-content font-serif-legal focus:outline-none px-2 py-2" },
    },
    onUpdate: ({ editor }) => setHtml(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && caseData.petition_html && caseData.petition_html !== editor.getHTML()) {
      editor.commands.setContent(caseData.petition_html);
      setHtml(caseData.petition_html);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  const placeholders = useMemo(() => countPlaceholders(html), [html]);

  const [fills, setFills] = useState({});
  const placeholderList = useMemo(
    () => [...new Set((html.match(/\[INSERIR[^\]]*\]/gi) || []))], [html]);

  const applyFill = (ph, val) => {
    const v = (val || "").trim();
    if (!v) return;
    const nh = html.split(ph).join(v);
    editor?.commands.setContent(nh);
    setHtml(nh);
    setFills((f) => { const n = { ...f }; delete n[ph]; return n; });
    toast.success("Marcador preenchido");
  };

  const applyAllFills = () => {
    let nh = html;
    Object.entries(fills).forEach(([ph, val]) => { if ((val || "").trim()) nh = nh.split(ph).join(val.trim()); });
    editor?.commands.setContent(nh);
    setHtml(nh);
    setFills({});
    toast.success("Marcadores aplicados");
  };

  const generate = async () => {
    if (!(caseData.claims || []).length) {
      toast.error("Selecione ao menos um pedido antes de gerar a peça");
      return;
    }
    setGenerating(true);
    try {
      const res = await api.post(`/cases/${caseData.id}/generate`);
      editor?.commands.setContent(res.data.petition_html);
      setHtml(res.data.petition_html);
      toast.success("Petição gerada");
      onSaved({ ...caseData, petition_html: res.data.petition_html, status: "gerado" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao gerar petição");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/cases/${caseData.id}`, { petition_html: html });
      toast.success("Petição salva");
      onSaved({ ...caseData, petition_html: html });
    } catch (e) {
      toast.error("Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const exportDoc = async (fmt) => {
    try {
      const res = await api.get(`/cases/${caseData.id}/export/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `peticao.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exportado em ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error("Falha ao exportar. Gere a petição primeiro.");
    }
  };

  const ToolbarBtn = ({ onClick, active, children, testid }) => (
    <button data-testid={testid} onClick={onClick} type="button"
      className={`h-8 w-8 flex items-center justify-center rounded hover:bg-slate-200 transition ${active ? "bg-slate-900 text-white hover:bg-slate-800" : "text-slate-700"}`}>
      {children}
    </button>
  );

  const hasContent = !!(html && html.replace(/<[^>]*>/g, "").trim());

  return (
    <div className="space-y-4">
      {placeholders > 0 && (
        <div data-testid="risk-alert-banner"
          className="flex items-center gap-3 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-rose-800">
          <AlertTriangle className="h-5 w-5 shrink-0 text-rose-600" />
          <p className="text-sm font-medium">
            Atenção: <strong>{placeholders}</strong> marcador(es) <code className="font-bold">[INSERIR XXX]</code> ainda não preenchido(s). Resolva antes de protocolar.
          </p>
        </div>
      )}

      {placeholderList.length > 0 && (
        <div className="border rounded-xl bg-white p-4" data-testid="placeholders-panel">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-display font-bold text-sm">Preencher marcadores pendentes ({placeholderList.length})</h4>
            <Button size="sm" variant="outline" onClick={applyAllFills} data-testid="apply-all-fills-btn">Aplicar todos</Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {placeholderList.map((ph, idx) => (
              <div key={ph} className="flex gap-2 items-center">
                <code className="text-[11px] text-rose-700 bg-rose-50 px-2 py-1.5 rounded whitespace-nowrap max-w-[42%] truncate" title={ph}>{ph}</code>
                <Input data-testid={`fill-input-${idx}`} value={fills[ph] || ""}
                  onChange={(e) => setFills((f) => ({ ...f, [ph]: e.target.value }))}
                  placeholder="valor" className="h-9 bg-white flex-1" />
                <Button size="sm" onClick={() => applyFill(ph, fills[ph])} data-testid={`apply-fill-${idx}`}
                  className="bg-slate-900 hover:bg-slate-800 text-white">OK</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 justify-between">
        <Button data-testid="generate-petition-btn" onClick={generate} disabled={generating}
          className="bg-amber-600 hover:bg-amber-700 text-white gap-2">
          {generating ? <><Loader2 className="h-4 w-4 animate-spin" /> Gerando…</> : <><Sparkles className="h-4 w-4" /> {hasContent ? "Regerar petição" : "Gerar petição"}</>}
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={save} disabled={saving} data-testid="save-petition-btn" className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar
          </Button>
          <Button variant="outline" onClick={() => exportDoc("docx")} data-testid="export-docx-btn" className="gap-2">
            <FileText className="h-4 w-4" /> DOCX
          </Button>
          <Button onClick={() => exportDoc("pdf")} data-testid="export-pdf-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white gap-2">
            <FileDown className="h-4 w-4" /> PDF
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Editor */}
        <div className="lg:col-span-7 border rounded-xl bg-white flex flex-col shadow-sm overflow-hidden">
          <div className="flex items-center gap-1 border-b bg-slate-50 px-2 py-1.5">
            <ToolbarBtn testid="fmt-bold" onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive("bold")}><Bold className="h-4 w-4" /></ToolbarBtn>
            <ToolbarBtn testid="fmt-italic" onClick={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive("italic")}><Italic className="h-4 w-4" /></ToolbarBtn>
            <ToolbarBtn testid="fmt-underline" onClick={() => editor?.chain().focus().toggleUnderline().run()} active={editor?.isActive("underline")}><UnderlineIcon className="h-4 w-4" /></ToolbarBtn>
            <div className="w-px h-5 bg-slate-300 mx-1" />
            <ToolbarBtn testid="fmt-h2" onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} active={editor?.isActive("heading", { level: 2 })}><Heading2 className="h-4 w-4" /></ToolbarBtn>
            <ToolbarBtn testid="fmt-h3" onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()} active={editor?.isActive("heading", { level: 3 })}><Heading3 className="h-4 w-4" /></ToolbarBtn>
            <ToolbarBtn testid="fmt-list" onClick={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive("bulletList")}><List className="h-4 w-4" /></ToolbarBtn>
          </div>
          <div className="p-4 overflow-y-auto max-h-[70vh]" data-testid="petition-editor">
            {!hasContent && !generating ? (
              <div className="text-center py-20 text-muted-foreground">
                <Sparkles className="h-8 w-8 mx-auto mb-3 text-amber-500" />
                <p className="text-sm">Clique em <strong>Gerar petição</strong> para criar a minuta a partir dos dados e pedidos.</p>
              </div>
            ) : (
              <EditorContent editor={editor} />
            )}
          </div>
        </div>

        {/* Live Preview */}
        <div className="lg:col-span-5 border rounded-xl bg-slate-100 p-6 overflow-y-auto max-h-[75vh] shadow-inner">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-4">Pré-visualização</p>
          <div className="bg-white rounded-lg shadow-sm p-8 min-h-[400px]">
            <div className="text-center font-display font-extrabold text-sm mb-6 uppercase" data-testid="petition-header-firm">Silvestre&amp;Maia - IA</div>
            <div className="petition-content font-serif-legal text-[13px] text-slate-800"
              data-testid="petition-preview"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(highlightPlaceholders(html) || "<p class='text-center text-slate-400'>A petição aparecerá aqui.</p>", { ADD_ATTR: ["style"] }) }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function highlightPlaceholders(html) {
  if (!html) return "";
  return html.replace(/(\[INSERIR[^\]]*\])/gi,
    '<span style="background:#fee2e2;color:#b91c1c;padding:0 3px;border-radius:3px;font-weight:700">$1</span>');
}
